import * as React from 'react';

import { Query } from '@apollo/client/react/components';

import type EntityContext from 'common/constants/entityContexts';
import type Permissions from 'common/constants/permissions';

import context from './PermissionsContext';
import type { PermissionsQueryVariables, PermissionsResponse } from './queries';
import { PermissionsQuery } from './queries';

export interface PermissionsSubscription {
  entityId?: string;
  context?: EntityContext;
  permissionNames?: Permissions[];
}

export interface PermissionsProviderState {
  subscriptions: Map<string, PermissionsSubscription>;
}

export default class PermissionsProvider extends React.PureComponent<
  React.PropsWithChildren<unknown>,
  PermissionsProviderState
> {
  state = { subscriptions: new Map<string, PermissionsSubscription>() };

  private getVariables(): PermissionsQueryVariables {
    // Creates a map that maps the entity to all unique permissions required for it.
    const scopeMap = Array.from(this.state.subscriptions.values()).reduce((scopeAcc, subscription) => {
      const subscriptionKey = `${subscription.entityId}:${subscription.context}`;
      const scope = scopeAcc.get(subscriptionKey);

      if (scope?.permissionNames && subscription.permissionNames) {
        subscription.permissionNames?.forEach(item => scope.permissionNames?.add(item));
      } else if (!scope) {
        scopeAcc.set(subscriptionKey, {
          entityId: subscription.entityId,
          context: subscription.context,
          permissionNames: subscription.permissionNames ? new Set(subscription.permissionNames) : undefined,
        });
      } else {
        scopeAcc.set(subscriptionKey, {
          entityId: subscription.entityId,
          context: subscription.context,
          permissionNames: undefined,
        });
      }

      return scopeAcc;
    }, new Map<string, { entityId?: string; context?: EntityContext; permissionNames?: Set<Permissions> }>());

    // Flatten the permissionNames sets into arrays and return all the scopes
    return {
      scopes: Array.from(scopeMap.values()).map(scope => ({
        entityId: scope.entityId,
        context: scope.context,
        permissionNames: scope.permissionNames && Array.from(scope.permissionNames),
      })),
    };
  }

  private registerPermissions = (subscription: PermissionsSubscription) => {
    const subscriptionId = crypto.randomUUID();
    this.setState(prevState => {
      const subscriptions = new Map<string, PermissionsSubscription>(prevState.subscriptions);
      subscriptions.set(subscriptionId, subscription);
      return { subscriptions };
    });
    return subscriptionId;
  };

  private unregisterPermissions = (subscriptionId: string) => {
    this.setState(prevState => {
      const subscriptions = new Map<string, PermissionsSubscription>(prevState.subscriptions);
      subscriptions.delete(subscriptionId);
      return { subscriptions };
    });
  };

  render() {
    const variables = this.getVariables();
    return (
      <Query
        query={PermissionsQuery}
        variables={variables}
        skip={variables.scopes.length === 0}
        fetchPolicy="network-only"
      >
        {({ data, loading }: { data?: PermissionsResponse; loading: boolean }) => (
          <context.Provider
            value={{
              permissions: data?.permissionScopes ?? [],
              registerPermissions: this.registerPermissions,
              unregisterPermissions: this.unregisterPermissions,
              loading,
              variables,
            }}
          >
            {this.props.children}
          </context.Provider>
        )}
      </Query>
    );
  }
}
